import { ArrowBackIos, GpsFixed, Phone } from '@mui/icons-material'
import { Box, Button, Chip, IconButton, TextField, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { pdfjs } from 'react-pdf'
import { useNavigate, useParams } from 'react-router-dom'
import { getReadableOperationType } from '../../domain/Animal/AnimalDisplay'
import { AnimalProfilesClient } from '../../interactors/clients/AnimalProfilesClient'
import { ServiceLocationsClient } from '../../interactors/clients/ServiceLocationsClient'
import { VeterinaryClient } from '../../interactors/clients/VeterinaryClient'
import {
  AnimalProfileDocumentDto,
  AnimalProfileDto,
  AnimalProfileHealthOperationDto,
  ServiceLocationWithProvidersNamesDto,
} from '../../interactors/gen/backendClient'
import { fromDayNumberToName } from '../../utils/date/fromDayNumbeToName'
import { fromMonthNumberToName } from '../../utils/date/fromMonthNumberToName'
import { getIconForOperationType } from '../../utils/getIconForOperation'
import { CircularProgressPanel } from '../common/CircularProgressPanel'
import { colors, lightBlue, theme } from '../theme'
import { OperationDocumentCard } from '../Animals/OperationDocumentCard'
import { Comment } from './Comment'
import { AddComment } from './AddComment'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString()

export const SuiviOperationDetailsScreen: React.FC = () => {
  const [operation, setOperation] = React.useState<AnimalProfileHealthOperationDto | null>(null)
  const [animal, setAnimal] = React.useState<AnimalProfileDto | null>(null)
  const [documents, setDocuments] = React.useState<AnimalProfileDocumentDto[]>([])
  const [veterinaryName, setVeterinaryName] = React.useState<string>('Vétérinaire inconnu(e)')
  const [externalVeterinary, setExternalVeterinary] = React.useState<ServiceLocationWithProvidersNamesDto | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const { operationId, id } = useParams<{ operationId: string; id: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchVeterinary = async (operation: AnimalProfileHealthOperationDto) => {
      // Ensure operation and veterinary object exist
      if (!operation || !operation.veterinary) return

      const veterinaryId = operation.veterinary.id
      const locationId = operation.veterinary.locationId

      if (!veterinaryId) return

      try {
        // If veterinaryId starts with 'A-', it's a Petso veterinary
        if (veterinaryId && veterinaryId.startsWith('A-')) {
          const veterinary = await VeterinaryClient.getVeterinaryById(veterinaryId)
          setVeterinaryName(`Docteur ${veterinary.firstName} ${veterinary.lastName}`)
        }
        // If locationId is provided, it indicates an external veterinary linked to a service location
        else if (locationId && locationId.startsWith('L-')) {
          const clinicExport = await ServiceLocationsClient.getLocation(locationId)
          // Assuming operation.veterinary.id is the id of the veterinary in the external clinic
          const veterinary = clinicExport.serviceProvidersNames.find((vet) => vet.id === veterinaryId)

          if (veterinary) {
            setVeterinaryName(`Docteur ${veterinary.firstName} ${veterinary.lastName}`)
            setExternalVeterinary(clinicExport)
          }
        }
      } catch (error) {
        console.error('Error fetching veterinary information:', error)
        // Handle errors or set state accordingly if needed
      }
    }

    const fetchOperationAndVeterinary = async () => {
      try {
        if (!id || !operationId) return

        const animal = await AnimalProfilesClient.getAnimalByMongoId(id)
        const operation = animal.health.operations?.find((op) => op.id === operationId)

        if (animal && operation) {
          setAnimal(animal)
          setOperation(operation)
          if (operation.documentsIds && operation.documentsIds.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            setDocuments(animal.documents.filter((doc) => operation.documentsIds!.includes(doc.id)))
          }
          await fetchVeterinary(operation) // Ensure this is called after setting the operation
        }
      } catch (error) {
        console.error('Error fetching operation information:', error)
        // Handle errors or set state accordingly if needed
      } finally {
        setIsLoading(false)
      }
    }

    fetchOperationAndVeterinary()
  }, [id, operationId, operation?.veterinary?.id])

  if (isLoading || !animal || !operation) return <CircularProgressPanel />

  return (
    <Box sx={{ padding: { xs: 0, md: 2 }, minHeight: '100%', pb: 20, overflowY: 'auto' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          marginX: 'auto',
          mb: 10,
          p: 2,
          pb: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '24px',
            color: colors.black,
          }}
        >
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIos sx={{ fontSize: '16px', color: theme.palette.secondary.main }} />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: '8px',
              px: '16px',
              borderRadius: '8px',
              gap: '10px',
              backgroundColor: colors.lightGrey,
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '16px',
                  color: theme.palette.secondary.main,
                }}
              >
                {getIconForOperationType(operation.type[0])}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                    color: colors.black,
                  }}
                >
                  {operation.type.map((type) => (
                    <Chip key={type} sx={{ backgroundColor: lightBlue }} label={getReadableOperationType(type)} />
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '18px',
                  color: colors.black,
                }}
              >
                <Typography>{`${fromDayNumberToName(dayjs(operation.date, 'DD/MM/YYYY').day())} ${operation.date.split(
                  '/',
                  1
                )} ${fromMonthNumberToName(dayjs(operation.date, 'DD/MM/YYYY').month())} ${
                  operation.date.split('/', 3)[2]
                }`}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', py: '8px', px: '16px', width: '100%' }}>
          <Typography sx={{ fontSize: '18px' }}>{`Notes`}</Typography>
          <TextField
            multiline
            placeholder="Notes"
            value={operation.additionalInfo}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '100%',
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: '8px',
            px: '16px',
            borderRadius: '8px',
            gap: '16px',
            backgroundColor: colors.lightGrey,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
            <Typography sx={{ fontSize: '24px' }}>👨‍⚕️</Typography>
            <Typography sx={{ fontSize: '20px' }}>{veterinaryName}</Typography>
          </Box>
          {externalVeterinary && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', width: '100%' }}>
              <Typography sx={{ fontSize: '16px' }}>{externalVeterinary.name}</Typography>
            </Box>
          )}
          {externalVeterinary && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
              }}
            >
              <Typography
                sx={{ fontSize: '16px' }}
              >{`${externalVeterinary.postalCode}, ${externalVeterinary.city}`}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '32px' }}>
                <Button
                  color="secondary"
                  variant="contained"
                  sx={{
                    borderRadius: '10px',
                    p: '10px',
                    minWidth: '0',
                  }}
                  component="a"
                  href={`tel:${externalVeterinary.phoneNumber}`}
                >
                  <Phone />
                </Button>

                <Button
                  color="secondary"
                  variant="contained"
                  sx={{
                    borderRadius: '10px',
                    p: '10px',
                    minWidth: '0',
                  }}
                  component="a"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${externalVeterinary.postalCode}+${externalVeterinary.city}+${externalVeterinary.address}`}
                  target="_blank" // Opens in a new tab
                >
                  <GpsFixed />
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            width: '100%',
            borderRadius: '8px',
            p: '10px',
            backgroundColor: lightBlue,
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <Typography sx={{ fontSize: '18px' }}>{`Documents liés`}</Typography>
          </Box>

          {documents.map((document) => (
            <OperationDocumentCard key={document.id} document={document} />
          ))}
        </Box>

        {operation.comments && operation.comments.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', py: '8px', width: '100%' }}>
            <Typography sx={{ fontSize: '18px' }}>{`Compte rendu`}</Typography>

            {operation.comments.map((comment, index) => (
              <Comment key={`${comment.name}-${index}`} comment={comment} />
            ))}
          </Box>
        )}
        <AddComment
          type="operation"
          animalId={animal.id}
          id={operationId!}
          setAnimal={setAnimal}
          setOperation={setOperation}
        />
      </Box>
    </Box>
  )
}
